import { base44 } from '@/api/base44Client';

export const normalizeClean = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

export const findBahanMatch = (namaBahan, kategori = '', bahanList = []) => {
  if (!namaBahan) return null;
  const targetNorm = normalizeClean(namaBahan);
  const targetLower = namaBahan.toLowerCase().trim();

  // 1. Exact or standard include match
  let found = (bahanList || []).find(b => {
    const bNama = (b.nama || '').toLowerCase().trim();
    return bNama === targetLower || bNama.includes(targetLower) || targetLower.includes(bNama);
  });
  if (found) return found;

  // 2. Normalized alphanum match (ignores whitespace, hyphens, slashes)
  found = (bahanList || []).find(b => {
    const bNorm = normalizeClean(b.nama);
    return bNorm === targetNorm || bNorm.includes(targetNorm) || targetNorm.includes(bNorm);
  });
  if (found) return found;

  // 3. Fallback by generic category keywords
  const catLower = (kategori || '').toLowerCase().trim();
  const stdCategories = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain'];
  for (const std of stdCategories) {
    if (catLower.includes(std) || targetLower.includes(std)) {
      found = (bahanList || []).find(b => {
        const bCat = (b.kategori || '').toLowerCase().trim();
        const bNorm = normalizeClean(b.nama);
        return (bCat.includes(std) || bNorm.includes(std));
      });
      if (found) return found;
    }
  }

  return null;
};

/**
 * Kurangi stok berdasarkan jenis tugas yang selesai.
 * @param {object} order - data ProductionOrder lengkap
 * @param {string} activityType - racik | filling | packaging | etc
 * @param {Array}  inventoryItems - array gabungan inventori
 * @param {Array}  bahanList - array BahanCair
 * @returns {{ deducted: number, errors: number, skipped: boolean }}
 */
/**
 * Kurangi stok berdasarkan jenis tugas yang selesai.
 * @param {object} order - data ProductionOrder lengkap
 * @param {string} activityType - racik | mixing | filling | packing | packaging | labeling | sealing
 * @param {Array}  inventoryItems - array gabungan inventori
 * @param {Array}  bahanList - array BahanCair
 * @returns {{ deducted: number, errors: number, skipped: boolean }}
 */
export async function deductStockForTask(order, activityType, inventoryItems, bahanList, targetVariantName = null) {
  if (!order || !order.id) return { deducted: 0, errors: 0, skipped: true };

  const normalizedActivity = (activityType || '').toLowerCase();
  let categoryKey = 'other';
  if (normalizedActivity === 'racik' || normalizedActivity === 'mixing') categoryKey = 'racik';
  else if (normalizedActivity === 'filling') categoryKey = 'filling';
  else if (['packing', 'packaging', 'labeling', 'sealing'].includes(normalizedActivity)) categoryKey = 'packaging';

  // Tracking key: jika per-varian, gunakan key spesifik varian
  const trackKey = targetVariantName ? `${categoryKey}_${normalizeClean(targetVariantName)}` : categoryKey;
  const deductedTracker = { ...(order.task_stock_deducted || {}) };
  if (deductedTracker[trackKey] || deductedTracker[categoryKey]) {
    return { deducted: 0, errors: 0, skipped: true }; // PASTI dilewati, TIDAK AKAN double deduction!
  }

  let deducted = 0;
  let errors = 0;

  // Tentukan kategori apa yang harus dikurangi berdasarkan activity
  const categoryMap = {
    racik: ['bibit', 'bahan', 'bahan_cair', 'dpg', 'alkohol'],
    filling: ['botol', 'tutup', 'spray'],
    packaging: ['box', 'sticker', 'gift_card', 'kemasan_lainnya', 'dus', 'label', 'stiker']
  };

  const targetCategories = categoryMap[categoryKey] || [];

  if (targetCategories.length > 0) {
    // Tentukan sumber komponen & racikan (jika ada targetVariantName, ambil khusus varian tersebut)
    let sourceComponents = order.components || [];
    let sourceRacikan = order.racikan_digunakan || [];

    if (targetVariantName && Array.isArray(order.products) && order.products.length > 0) {
      const vNorm = normalizeClean(targetVariantName);
      const matchedProd = order.products.find(p => {
        const pNorm = normalizeClean(p.product_name || p.variant_name || '');
        return pNorm === vNorm || pNorm.includes(vNorm) || vNorm.includes(pNorm);
      });

      if (matchedProd) {
        if (Array.isArray(matchedProd.components) && matchedProd.components.length > 0) {
          sourceComponents = matchedProd.components;
        }
        if (Array.isArray(matchedProd.racikan_digunakan) && matchedProd.racikan_digunakan.length > 0) {
          sourceRacikan = matchedProd.racikan_digunakan;
        } else if (Array.isArray(matchedProd.racikan) && matchedProd.racikan.length > 0) {
          sourceRacikan = matchedProd.racikan;
        }
      }
    }

    const hasRacikanDigunakan = categoryKey === 'racik' && Array.isArray(sourceRacikan) && sourceRacikan.length > 0;
    const racikanNames = new Set(
      hasRacikanDigunakan 
        ? sourceRacikan.map(r => (r.nama_bahan || '').toLowerCase().trim()).filter(Boolean)
        : []
    );

    const integratedComps = (sourceComponents || []).filter(c => {
      if (!c || (!c.inventory_item_id && !c.item_name) || !(Number(c.qty_needed) > 0)) return false;
      const cCat = (c.category || c.name || '').toLowerCase();
      const isTargetCat = targetCategories.some(tc => cCat.includes(tc) || tc.includes(cCat));
      if (!isTargetCat) return false;

      // Jika kategori racik dan bahan ini sudah ada di racikan_digunakan, lewati dari components agar tidak dobel
      if (hasRacikanDigunakan) {
        const cNameLower = (c.item_name || '').toLowerCase().trim();
        if (racikanNames.has(cNameLower)) return false;
      }
      return true;
    });

    // Aggregate qty per matched inventory item
    const compQtyMap = {};
    for (const comp of integratedComps) {
      const matchedInv = inventoryItems.find(i => 
        (comp.inventory_item_id && (String(i.id) === String(comp.inventory_item_id) || String(i._stokId) === String(comp.inventory_item_id))) ||
        (comp.item_name && i.item_name && i.item_name.toLowerCase().trim() === comp.item_name.toLowerCase().trim())
      );

      const targetId = matchedInv ? (matchedInv._stokId || matchedInv.id) : comp.inventory_item_id;
      if (targetId) {
        if (!compQtyMap[targetId]) compQtyMap[targetId] = { invItem: matchedInv, qty: 0 };
        compQtyMap[targetId].qty += Number(comp.qty_needed || 0);
      }
    }

    for (const itemId of Object.keys(compQtyMap)) {
      const { invItem, qty } = compQtyMap[itemId];
      if (invItem && qty > 0) {
        const currentStock = Number(invItem.total_stock ?? invItem.stok ?? 0);
        const newStock = Math.max(0, currentStock - qty);
        
        if (invItem._entity === 'Botol')       await base44.entities.Botol.update(invItem._stokId || invItem.id, { stok: newStock }).catch(() => {});
        else if (invItem._entity === 'Tutup')  await base44.entities.Tutup.update(invItem._stokId || invItem.id, { stok: newStock }).catch(() => {});
        else if (invItem._entity === 'Spray')  await base44.entities.Spray.update(invItem._stokId || invItem.id, { stok: newStock }).catch(() => {});
        else if (invItem._entity === 'BahanCair') await base44.entities.BahanCair.update(invItem._stokId || invItem.id, { stok: newStock }).catch(() => {});
        else if (invItem._entity === 'Fullset') await base44.entities.Fullset.update(invItem._stokId || invItem.id, { stok: newStock }).catch(() => {});
        else await base44.entities.InventoryItem.update(invItem.id || itemId, { total_stock: newStock }).catch(() => {});
        
        // Update in-memory
        invItem.total_stock = newStock;
        if (invItem.stok !== undefined) invItem.stok = newStock;

        // Otomatis catat ke log Barang Keluar (DeliveryItem)
        if (base44.entities.DeliveryItem?.create) {
          base44.entities.DeliveryItem.create({
            jenis: 'keluar',
            tanggal: new Date().toISOString(),
            no_po: order.order_number || `PO-${order.id?.slice(-6)}`,
            nama_barang: invItem.nama || invItem.name || invItem.item_name || 'Komponen PO',
            kategori: invItem._entity || invItem.kategori || categoryKey,
            jumlah: qty,
            satuan: invItem.satuan || 'pcs',
            catatan: `Pemotongan otomatis tahap ${categoryKey.toUpperCase()}${targetVariantName ? ` (${targetVariantName})` : ''} — PO #${order.order_number || order.id} (${order.brand_name || ''})`,
            status: 'sudah_sampai'
          }).catch(() => {});
        }
        
        deducted++;
      } else if (qty > 0) {
        errors++;
      }
    }

    // Khusus untuk racik/mixing, kurangi juga dari racikan_digunakan (bahan cair)
    if (categoryKey === 'racik' && hasRacikanDigunakan) {
      const racikanQtyMap = {};
      
      for (const r of sourceRacikan) {
        if (!r.nama_bahan || !(Number(r.kebutuhan_nilai) > 0)) continue;
        const nameLower = r.nama_bahan.toLowerCase().trim();
        
        const bahanItem = findBahanMatch(r.nama_bahan, r.kategori_bahan, bahanList);

        if (bahanItem) {
          let kebutuhan = Number(r.kebutuhan_nilai) || 0;
          const satuan = (r.kebutuhan_satuan || r.satuan || bahanItem.satuan || '').toLowerCase();
          if (satuan === 'g' || satuan === 'gram' || satuan === 'ml') kebutuhan = kebutuhan / 1000;
          
          if (!racikanQtyMap[bahanItem.id]) racikanQtyMap[bahanItem.id] = { item: bahanItem, totalQty: 0 };
          racikanQtyMap[bahanItem.id].totalQty += kebutuhan;
        } else {
          errors++;
        }
      }

      for (const bId of Object.keys(racikanQtyMap)) {
        const { item, totalQty } = racikanQtyMap[bId];
        const currentStock = Number(item.stok ?? item.total_stock ?? 0);
        const newStock = Math.max(0, currentStock - totalQty);
        await base44.entities.BahanCair.update(item.id, { stok: newStock }).catch(() => {});
        item.stok = newStock;

        // Otomatis catat ke log Barang Keluar (DeliveryItem)
        if (base44.entities.DeliveryItem?.create) {
          base44.entities.DeliveryItem.create({
            jenis: 'keluar',
            tanggal: new Date().toISOString(),
            no_po: order.order_number || `PO-${order.id?.slice(-6)}`,
            nama_barang: item.nama || 'Bahan Cair',
            kategori: 'Bahan Cair',
            jumlah: Number(totalQty.toFixed(3)),
            satuan: item.satuan || 'kg',
            catatan: `Pemotongan otomatis bahan mixing${targetVariantName ? ` (${targetVariantName})` : ''} — PO #${order.order_number || order.id} (${order.brand_name || ''})`,
            status: 'sudah_sampai'
          }).catch(() => {});
        }

        deducted++;
      }
    }
  }

  // 3. Tandai PO sudah dikurangi stoknya untuk kategori tugas ini
  deductedTracker[trackKey] = true;
  await base44.entities.ProductionOrder.update(order.id, {
    task_stock_deducted: deductedTracker,
  }).catch(() => {});

  return { deducted, errors, skipped: false };
}

/**
 * Kurangi seluruh sisa stok bahan dan kemasan untuk PO jika diselesaikan langsung (tanpa double deduction)
 * serta otomatis catat histori Barang Keluar jika belum pernah dicatat.
 */
export async function deductStockForPO(order, inventoryItems, bahanList, trigger = 'done') {
  if (!order || !order.id) return { deducted: 0, errors: 0, skipped: true };

  let totalDeducted = 0;
  let totalErrors = 0;

  // Jalankan pengurangan stok hanya untuk tahapan produksi yang BELUM terpotong
  for (const activityType of ['racik', 'filling', 'packaging']) {
    const res = await deductStockForTask(order, activityType, inventoryItems, bahanList);
    totalDeducted += res.deducted || 0;
    totalErrors += res.errors || 0;
  }

  // Otomatis catat ke riwayat Barang Keluar jika PO telah selesai/dikirim dan belum tercatat
  try {
    await recordBarangKeluarForPO(order);
  } catch (e) {
    console.error("Gagal mencatat riwayat Barang Keluar:", e);
  }

  return { deducted: totalDeducted, errors: totalErrors, skipped: false };
}

/**
 * Catat riwayat Barang Keluar untuk PO jika belum pernah dibuat
 */
export async function recordBarangKeluarForPO(po) {
  if (!po || !po.id || po.is_synced_to_delivery) return;

  // Cek apakah sudah pernah ada BarangKeluar untuk PO ini
  const existing = await base44.entities.BarangKeluar.filter({ source_po_id: po.id }).catch(() => []);
  if (existing.length > 0) {
    await base44.entities.ProductionOrder.update(po.id, { is_synced_to_delivery: true }).catch(() => {});
    return;
  }

  const items = [];

  // Komponen packaging
  (po.components || []).forEach(comp => {
    if (!comp.item_name) return;
    items.push({
      nama_barang: comp.item_name,
      kategori: comp.category || comp.name || 'Kemasan',
      jumlah: Number(comp.qty_needed) || 0,
      satuan: comp.unit || 'pcs',
      vendor: comp.vendor || '-',
      item_id: comp.inventory_item_id || '',
      item_tipe: comp.category || 'packaging',
    });
  });

  // Racikan / bahan cair
  (po.racikan_digunakan || []).forEach(r => {
    if (!r.nama_bahan) return;
    items.push({
      nama_barang: r.nama_bahan,
      kategori: r.kategori_bahan || 'Bahan Cair',
      jumlah: Number(r.kebutuhan_nilai) || 0,
      satuan: r.kebutuhan_satuan || r.satuan || 'g',
      vendor: r.vendor || '-',
      item_id: '',
      item_tipe: 'bahan',
    });
  });

  if (items.length === 0) return;

  await base44.entities.BarangKeluar.create({
    nomor_referensi: `PO-${po.order_number || po.id.slice(-6)}`,
    tanggal: po.shipped_at || po.shipping_date || new Date().toISOString().split('T')[0],
    tujuan: po.brand_name || '',
    alasan: 'Produksi (PO)',
    catatan: `Pemotongan Stok PO #${po.order_number || po.id} — ${po.brand_name} ${po.product_name || ''}`.trim(),
    source_po_id: po.id,
    items,
  });

  await base44.entities.ProductionOrder.update(po.id, { is_synced_to_delivery: true }).catch(() => {});
}

/**
 * Kembalikan (refund) stok yang sebelumnya dipotong oleh deductStockForTask.
 * @param {object} order - data ProductionOrder lengkap
 * @param {string} activityType - racik | filling | packaging | etc
 * @param {Array}  inventoryItems - array gabungan inventori
 * @param {Array}  bahanList - array BahanCair
 * @returns {{ refunded: number, errors: number, skipped: boolean }}
 */
export async function refundStockForTask(order, activityType, inventoryItems, bahanList) {
  const deductedTracker = order.task_stock_deducted || {};
  if (!deductedTracker[activityType]) {
    return { refunded: 0, errors: 0, skipped: true }; // Belum dikurangi, jadi tidak perlu di-refund
  }

  let refunded = 0;
  let errors = 0;

  const categoryMap = {
    racik: ['bibit', 'bahan'],
    filling: ['botol', 'tutup', 'spray'],
    packaging: ['box', 'sticker', 'gift_card', 'kemasan_lainnya']
  };

  const targetCategories = categoryMap[activityType] || [];

  if (targetCategories.length > 0) {
    const integratedComps = (order.components || []).filter(
      c => c.inventory_item_id && (c.qty_needed || 0) > 0 && targetCategories.includes(c.category)
    );

    const compQtyMap = {};
    for (const comp of integratedComps) {
      if (!compQtyMap[comp.inventory_item_id]) compQtyMap[comp.inventory_item_id] = 0;
      compQtyMap[comp.inventory_item_id] += comp.qty_needed;
    }

    for (const itemId of Object.keys(compQtyMap)) {
      const qty = compQtyMap[itemId];
      const invItem = inventoryItems.find(i => String(i.id) === String(itemId));
      if (invItem && qty > 0) {
        const newStock = (invItem.total_stock || 0) + qty; // TAMBAH stok
        if (invItem._entity === 'Botol')       await base44.entities.Botol.update(invItem._stokId, { stok: newStock });
        else if (invItem._entity === 'Tutup')  await base44.entities.Tutup.update(invItem._stokId, { stok: newStock });
        else if (invItem._entity === 'Spray')  await base44.entities.Spray.update(invItem._stokId, { stok: newStock });
        else if (invItem._entity === 'BahanCair') await base44.entities.BahanCair.update(invItem._stokId, { stok: newStock });
        else if (invItem._entity === 'Fullset') await base44.entities.Fullset.update(invItem._stokId, { stok: newStock });
        else await base44.entities.InventoryItem.update(itemId, { total_stock: newStock });
        
        invItem.total_stock = newStock;
        if (invItem.stok !== undefined) invItem.stok = newStock;
        refunded++;
      } else if (qty > 0) {
        errors++;
      }
    }

    if (activityType === 'racik') {
      const racikan = order.racikan_digunakan || [];
      const racikanQtyMap = {};
      for (const r of racikan) {
        if (!r.nama_bahan || !(r.kebutuhan_nilai > 0)) continue;
        const nameLower = r.nama_bahan.toLowerCase().trim();
        const bahanItem = bahanList.find(b => {
          const bNama = (b.nama || '').toLowerCase().trim();
          return bNama === nameLower || bNama.includes(nameLower) || nameLower.includes(bNama);
        });

        if (bahanItem) {
          let kebutuhan = r.kebutuhan_nilai || 0;
          const satuan = (r.kebutuhan_satuan || '').toLowerCase();
          if (satuan === 'g' || satuan === 'gram') kebutuhan = kebutuhan / 1000;
          else if (satuan === 'ml') kebutuhan = kebutuhan / 1000;
          
          if (!racikanQtyMap[bahanItem.id]) racikanQtyMap[bahanItem.id] = { item: bahanItem, totalQty: 0 };
          racikanQtyMap[bahanItem.id].totalQty += kebutuhan;
        } else {
          errors++;
        }
      }

      for (const bId of Object.keys(racikanQtyMap)) {
        const { item, totalQty } = racikanQtyMap[bId];
        const newStock = (item.stok || 0) + totalQty; // TAMBAH stok
        await base44.entities.BahanCair.update(item.id, { stok: newStock });
        item.stok = newStock;
        refunded++;
      }
    }

    if (activityType === 'packaging' && order.additional_components) {
      for (const addon of order.additional_components) {
        refunded++; 
      }
    }
  }

  // Hapus tracker agar tugas ini dianggap belum dipotong
  delete deductedTracker[activityType];
  await base44.entities.ProductionOrder.update(order.id, {
    task_stock_deducted: deductedTracker,
  });

  return { refunded, errors, skipped: false };
}

/**
 * Preview pemotongan stok sebelum dieksekusi (stok awal, jumlah terpotong, sisa stok, serta deteksi catch-up).
 * @param {object} order - data ProductionOrder
 * @param {string} activityType - 'racik' | 'mixing' | 'filling' | 'packing' | 'packaging' | 'all' | 'direct'
 * @param {Array} inventoryItems - list inventori gabungan
 * @param {Array} bahanList - list BahanCair
 * @returns {object} { items: Array, hasCatchup: boolean, catchupStages: Array, totalDeductions: number }
 */
export function previewStockDeduction(order, activityType = 'all', inventoryItems = [], bahanList = [], targetVariantName = null) {
  if (!order) return { items: [], hasCatchup: false, catchupStages: [], totalDeductions: 0, hasShortage: false, hasZeroStock: false, shortageItems: [] };

  const deductedTracker = order.task_stock_deducted || {};
  const normalizedAct = (activityType || '').toLowerCase();

  // Jika per varian, periksa apakah varian ini sudah pernah dipotong
  const trackKey = targetVariantName ? `${normalizedAct}_${normalizeClean(targetVariantName)}` : normalizedAct;
  if (deductedTracker[trackKey] || (deductedTracker[normalizedAct] && !targetVariantName)) {
    return { items: [], hasCatchup: false, catchupStages: [], totalDeductions: 0, hasShortage: false, hasZeroStock: false, shortageItems: [] };
  }

  let stagesToPreview = [];
  let catchupStages = [];

  if (normalizedAct === 'all' || normalizedAct === 'direct' || normalizedAct === 'done' || normalizedAct === 'shipped') {
    if (!deductedTracker.racik) stagesToPreview.push('racik');
    if (!deductedTracker.filling) stagesToPreview.push('filling');
    if (!deductedTracker.packaging) stagesToPreview.push('packaging');
  } else if (normalizedAct === 'racik' || normalizedAct === 'mixing') {
    if (!deductedTracker.racik) stagesToPreview.push('racik');
  } else if (normalizedAct === 'filling') {
    if (!deductedTracker.racik) {
      stagesToPreview.push('racik');
      catchupStages.push('Mixing / Racik');
    }
    if (!deductedTracker.filling) stagesToPreview.push('filling');
  } else if (['packing', 'packaging', 'labeling', 'sealing'].includes(normalizedAct)) {
    if (!deductedTracker.racik) {
      stagesToPreview.push('racik');
      catchupStages.push('Mixing / Racik');
    }
    if (!deductedTracker.filling) {
      stagesToPreview.push('filling');
      catchupStages.push('Filling');
    }
    if (!deductedTracker.packaging) stagesToPreview.push('packaging');
  }

  const stageCategoryMap = {
    racik: ['bibit', 'bahan', 'bahan_cair', 'dpg', 'alkohol'],
    filling: ['botol', 'tutup', 'spray'],
    packaging: ['box', 'sticker', 'gift_card', 'kemasan_lainnya', 'dus', 'label', 'stiker']
  };

  // Tentukan sumber komponen & racikan berdasarkan targetVariantName jika ada
  let sourceComponents = order.components || [];
  let sourceRacikan = order.racikan_digunakan || [];

  if (targetVariantName && Array.isArray(order.products) && order.products.length > 0) {
    const vNorm = normalizeClean(targetVariantName);
    const matchedProd = order.products.find(p => {
      const pNorm = normalizeClean(p.product_name || p.variant_name || '');
      return pNorm === vNorm || pNorm.includes(vNorm) || vNorm.includes(pNorm);
    });

    if (matchedProd) {
      if (Array.isArray(matchedProd.components) && matchedProd.components.length > 0) {
        sourceComponents = matchedProd.components;
      }
      if (Array.isArray(matchedProd.racikan_digunakan) && matchedProd.racikan_digunakan.length > 0) {
        sourceRacikan = matchedProd.racikan_digunakan;
      } else if (Array.isArray(matchedProd.racikan) && matchedProd.racikan.length > 0) {
        sourceRacikan = matchedProd.racikan;
      }
    }
  }

  const previewItems = [];
  const processedKeys = new Set();

  for (const stg of stagesToPreview) {
    const isCatchupStage = (normalizedAct === 'filling' && stg === 'racik') || 
                           (['packing', 'packaging', 'labeling', 'sealing'].includes(normalizedAct) && (stg === 'racik' || stg === 'filling'));
    
    const stageLabel = stg === 'racik' ? 'Mixing / Racik' : stg === 'filling' ? 'Filling' : 'Pengemasan';
    const targetCats = stageCategoryMap[stg] || [];

    // 1. Dari components
    const hasRacikanDigunakan = stg === 'racik' && Array.isArray(sourceRacikan) && sourceRacikan.length > 0;
    const racikanNames = new Set(
      hasRacikanDigunakan 
        ? sourceRacikan.map(r => (r.nama_bahan || '').toLowerCase().trim()).filter(Boolean)
        : []
    );

    const matchedComps = (sourceComponents || []).filter(c => {
      if (!c || (!c.inventory_item_id && !c.item_name) || !(Number(c.qty_needed) > 0)) return false;
      const cCat = (c.category || c.name || '').toLowerCase();
      const isTargetCat = targetCats.some(tc => cCat.includes(tc) || tc.includes(cCat));
      if (!isTargetCat) return false;

      // Jika kategori racik dan bahan ini sudah ada di racikan_digunakan, lewati dari components agar tidak dobel kg/L
      if (hasRacikanDigunakan) {
        const cNameLower = (c.item_name || '').toLowerCase().trim();
        if (racikanNames.has(cNameLower)) return false;
      }
      return true;
    });

    for (const comp of matchedComps) {
      const matchedInv = inventoryItems.find(i => 
        (comp.inventory_item_id && (String(i.id) === String(comp.inventory_item_id) || String(i._stokId) === String(comp.inventory_item_id))) ||
        (comp.item_name && i.item_name && i.item_name.toLowerCase().trim() === comp.item_name.toLowerCase().trim())
      );

      const uniqueKey = comp.inventory_item_id || comp.item_name;
      if (processedKeys.has(uniqueKey)) continue;
      processedKeys.add(uniqueKey);

      const currentStock = Number(matchedInv?.total_stock ?? matchedInv?.stok ?? 0);
      const deductQty = Number(comp.qty_needed || 0);
      const remainingStock = Math.max(0, currentStock - deductQty);

      previewItems.push({
        id: uniqueKey,
        name: comp.item_name || matchedInv?.item_name || 'Item Kemasan',
        category: (comp.category || comp.name || 'Kemasan').toUpperCase(),
        stage: stageLabel,
        isCatchup: isCatchupStage,
        currentStock,
        deductQty,
        remainingStock,
        unit: comp.unit || 'pcs',
        alreadyDeducted: !!deductedTracker[stg],
        isShortage: currentStock < deductQty,
        isZero: currentStock <= 0
      });
    }

    // 2. Dari racikan_digunakan (Khusus tahap racik)
    if (stg === 'racik' && hasRacikanDigunakan) {
      for (const r of sourceRacikan) {
        if (!r.nama_bahan || !(Number(r.kebutuhan_nilai) > 0)) continue;
        const nameLower = r.nama_bahan.toLowerCase().trim();
        const uniqueKey = `racikan-${nameLower}`;
        if (processedKeys.has(uniqueKey)) continue;
        processedKeys.add(uniqueKey);

        const bahanItem = findBahanMatch(r.nama_bahan, r.kategori_bahan, bahanList);

        let rawNeeded = Number(r.kebutuhan_nilai) || 0;
        let displayUnit = (r.kebutuhan_satuan || r.satuan || bahanItem?.satuan || 'kg').toLowerCase();
        let currentStock = Number(bahanItem?.stok ?? bahanItem?.total_stock ?? 0);
        
        let deductAmount = rawNeeded;
        if (displayUnit === 'g' || displayUnit === 'gram') {
          deductAmount = rawNeeded / 1000;
          displayUnit = (bahanItem?.satuan || 'kg').toLowerCase();
        } else if (displayUnit === 'ml' || displayUnit === 'mililiter') {
          deductAmount = rawNeeded / 1000;
          displayUnit = (bahanItem?.satuan || 'L').toLowerCase();
        }

        const remainingStock = Math.max(0, currentStock - deductAmount);

        previewItems.push({
          id: uniqueKey,
          name: r.nama_bahan,
          category: (r.kategori_bahan || bahanItem?.kategori || 'Bahan Cair').toUpperCase(),
          stage: stageLabel,
          isCatchup: isCatchupStage,
          currentStock: Number(currentStock.toFixed(3)),
          deductQty: Number(deductAmount.toFixed(3)),
          remainingStock: Number(remainingStock.toFixed(3)),
          unit: bahanItem?.satuan || displayUnit,
          alreadyDeducted: !!deductedTracker.racik,
          isShortage: currentStock < deductAmount,
          isZero: currentStock <= 0
        });
      }
    }
  }

  const shortageItems = previewItems.filter(item => item.isShortage || item.isZero);
  const hasShortage = shortageItems.length > 0;
  const hasZeroStock = shortageItems.some(item => item.isZero);

  return {
    items: previewItems,
    hasCatchup: catchupStages.length > 0,
    catchupStages,
    totalDeductions: previewItems.length,
    alreadyFullyDeducted: stagesToPreview.length === 0,
    hasShortage,
    hasZeroStock,
    shortageItems
  };
}